import { useState, useRef, useEffect, useCallback } from 'react';
import { useSearchStore } from '../../stores/search-store.js';
import { useDeckStore } from '../../stores/deck-store.js';
import { useCardPreview } from '../../contexts/CardPreviewContext.js';
import { useAutocomplete } from '../../hooks/useAutocomplete.js';
import { useSearch } from '../../hooks/useSearch.js';

export function SearchBar() {
  const userInput = useSearchStore((s) => s.userInput);
  const results = useSearchStore((s) => s.results);
  const searchTrigger = useSearchStore((s) => s.searchTrigger);
  const setUserInput = useSearchStore((s) => s.setUserInput);
  const setAdvancedOpen = useSearchStore((s) => s.setAdvancedOpen);
  const clearSearch = useSearchStore((s) => s.clearSearch);
  const addCard = useDeckStore((s) => s.addCard);
  const entries = useDeckStore((s) => s.entries);
  const { setHoveredCard } = useCardPreview();
  const { execute } = useSearch();
  const { suggestions, cardsByName, clear: clearSuggestions } = useAutocomplete(userInput);

  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIdx, setHighlightedIdx] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Fire search when programmatically triggered (e.g. from card detail modal tag click)
  useEffect(() => {
    if (searchTrigger === 0) return;
    clearSuggestions();
    setShowSuggestions(false);
    setHighlightedIdx(-1);
    void execute(1);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTrigger]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setShowSuggestions(false);
        setHoveredCard(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [setHoveredCard]);

  const handleChange = (value: string) => {
    setUserInput(value);
    setHighlightedIdx(-1);
    setShowSuggestions(true);
  };

  const submitSearch = useCallback(
    (term?: string) => {
      if (term) setUserInput(term);
      clearSuggestions();
      setShowSuggestions(false);
      setHighlightedIdx(-1);
      setAdvancedOpen(false);
      setHoveredCard(null);
      void execute(1);
    },
    [setUserInput, clearSuggestions, setAdvancedOpen, execute, setHoveredCard],
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions || suggestions.length === 0) {
      if (e.key === 'Enter') submitSearch();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIdx((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIdx((i) => Math.max(i - 1, -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightedIdx >= 0 && suggestions[highlightedIdx]) {
        submitSearch(suggestions[highlightedIdx]);
      } else {
        submitSearch();
      }
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
      setHighlightedIdx(-1);
      setHoveredCard(null);
    }
  };

  const handleAddCard = (name: string) => {
    const card = cardsByName.get(name.toLowerCase());
    if (!card) return;
    addCard(card);
  };

  const isOpen = showSuggestions && suggestions.length > 0;

  return (
    <div ref={containerRef} className="relative">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <input
            ref={inputRef}
            type="text"
            value={userInput}
            onChange={(e) => handleChange(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
            placeholder="Search cards (e.g. ramp, counterspell, Llanowar…)"
            className="w-full bg-gray-800 border border-gray-700 rounded-lg pl-4 pr-8 py-2.5 text-sm text-gray-100 placeholder-gray-600 focus:outline-none focus:border-orange-500 transition-colors"
          />
          {(userInput || results) && (
            <button
              onClick={() => { clearSearch(); inputRef.current?.focus(); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-400 transition-colors"
              title="Clear search"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
        <button
          onClick={() => submitSearch()}
          className="px-4 py-2.5 bg-orange-600 hover:bg-orange-500 text-white text-sm font-medium rounded-lg transition-colors shrink-0"
        >
          Search
        </button>
      </div>

      {/* Autocomplete dropdown */}
      {isOpen && (
        <div className="absolute z-30 left-0 right-12 mt-1 bg-gray-800 border border-gray-700 rounded-lg shadow-xl overflow-hidden">
          {suggestions.map((s, i) => {
            const card = cardsByName.get(s.toLowerCase());
            const deckEntry = card ? entries.find((e) => e.card.oracleId === card.oracleId) : undefined;
            const isHighlighted = i === highlightedIdx;
            return (
              <div
                key={s}
                onMouseEnter={() => {
                  setHighlightedIdx(i);
                  if (card) setHoveredCard(card);
                }}
                onMouseLeave={() => setHoveredCard(null)}
                className={`flex items-center gap-2 px-4 py-2 text-sm transition-colors cursor-pointer ${
                  isHighlighted ? 'bg-orange-600 text-white' : 'text-gray-200 hover:bg-gray-700'
                }`}
                onMouseDown={(e) => {
                  // Clicks on the row (not on the + button) run the search
                  e.preventDefault();
                  submitSearch(s);
                }}
              >
                <span className="flex-1 truncate">{s}</span>
                {deckEntry !== undefined && (
                  <span
                    className={`text-[10px] tabular-nums shrink-0 font-bold ${
                      isHighlighted ? 'text-orange-100' : 'text-orange-400'
                    }`}
                    title={`In deck: ${deckEntry.quantity}`}
                  >
                    {deckEntry.quantity}
                  </span>
                )}
                <button
                  type="button"
                  disabled={!card}
                  onMouseDown={(e) => {
                    // Prevent the row's onMouseDown (which runs search)
                    e.preventDefault();
                    e.stopPropagation();
                    if (!card) return;
                    handleAddCard(s);
                  }}
                  title={card ? 'Add to deck' : 'Loading card…'}
                  className={`shrink-0 w-6 h-6 flex items-center justify-center text-xs font-bold rounded transition-colors ${
                    card
                      ? isHighlighted
                        ? 'bg-white/20 hover:bg-white/30 text-white'
                        : 'bg-orange-600/20 hover:bg-orange-600 text-orange-400 hover:text-white border border-orange-700/40 hover:border-orange-600'
                      : 'bg-gray-700/40 text-gray-600 cursor-not-allowed'
                  }`}
                >
                  +
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
